"use client"

import { useEffect, useState } from "react"
import { ref as storageRef, listAll, getDownloadURL } from "firebase/storage"
import { storage } from "../Firebase/firebase-config"
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId"
import { getDatabase, ref as dbRef, push, set, onValue } from "firebase/database"
import { FaDownload, FaFileAlt, FaClock } from "react-icons/fa"
import "./Downloads.css"
import { IoEyeSharp } from "react-icons/io5"
import { MdInfo } from "react-icons/md"

const db = getDatabase()

const getIndianTimestamp = () => {
  const now = new Date()

  const istTime = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
  )

  const day = String(istTime.getDate()).padStart(2, "0")
  const month = String(istTime.getMonth() + 1).padStart(2, "0")
  const year = istTime.getFullYear()

  const hours = String(istTime.getHours()).padStart(2, "0")
  const minutes = String(istTime.getMinutes()).padStart(2, "0")

  return `${day}/${month}/${year} ${hours}:${minutes}`
}

// Helper to get nice month name
const getMonthName = (date) => {
  return date.toLocaleString('en-IN', { 
    month: 'long', 
    year: 'numeric' 
  })
}

const getCurrentMonthRange = () => {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  
  return {
    from: firstDay.toISOString().split('T')[0],
    to: lastDay.toISOString().split('T')[0],
    displayLabel: getMonthName(now)
  }
}

const getPreviousMonthRange = () => {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const firstDay = new Date(prev.getFullYear(), prev.getMonth(), 1)
  const lastDay = new Date(prev.getFullYear(), prev.getMonth() + 1, 0)
  
  return {
    from: firstDay.toISOString().split('T')[0],
    to: lastDay.toISOString().split('T')[0],
    displayLabel: getMonthName(prev)
  }
}
const toOrdinal = (num) => {
  const n = parseInt(num, 10)
  if (isNaN(n)) return num
  if (n % 100 >= 11 && n % 100 <= 13) return n + "th"
  switch (n % 10) {
    case 1: return n + "st"
    case 2: return n + "nd"
    case 3: return n + "rd"
    default: return n + "th"
  }
}

const isValidWeek = (num) => num >= 1 && num <= 52

const formatDetails = (type, input) => {
  let trimmed = input.trim()
  if (!trimmed) return trimmed

  if (/[a-zA-Z]/.test(trimmed)) return trimmed

  const year = new Date().getFullYear()
  trimmed = trimmed.replace(/\s+/g, "")
  const segments = trimmed.split(",")
  const results = []

  for (let seg of segments) {
    if (seg.includes("-")) {
      const [startRaw, endRaw] = seg.split("-")
      const start = parseInt(startRaw, 10)
      const end = parseInt(endRaw, 10)

      if (isNaN(start) || isNaN(end) || !isValidWeek(start) || !isValidWeek(end) || start > end) {
        alert("Invalid week range")
        return input
      }
      results.push(`${toOrdinal(start)}–${toOrdinal(end)}`)
    } else {
      const num = parseInt(seg, 10)
      if (isNaN(num) || !isValidWeek(num)) {
        alert("Week number must be between 1 and 52")
        return input
      }
      results.push(toOrdinal(num))
    }
  }

  if (type === "mtc") {
    return results.length === 1 
      ? `Week ${results[0]}` 
      : `Week ${results.slice(0, -1).join(", ")} & ${results.slice(-1)}`
  }

  return results.length === 1 
    ? `${results[0]} Quarter ${year}` 
    : `${results.slice(0, -1).join(", ")} & ${results.slice(-1)} Quarter ${year}`
}

const formatDateForDisplay = (isoDate) => {
  if (!isoDate) return ""
  const [year, month, day] = isoDate.split("-").map(num => parseInt(num, 10))
  return `${day}-${month}-${year}`
}

// ==================== NOTIFICATION FUNCTION (RESTORED) ====================
const sendRequestNotification = async (effectiveUserId, companyName, category, rawDetails) => {
  const formattedDetails = formatDetails(category.toLowerCase(), rawDetails)
  
  const notificationRef = dbRef(db, `supplier_notifications/Docs_Request/${effectiveUserId}`)
  const newNotifRef = push(notificationRef)

  let requestType = "Document"
  if (category === "MTC") requestType = "MTC"
  else if (category === "STATEMENTS") requestType = "Statement"
  else if (category === "INVOICES") requestType = "Invoice"
  else if (category === "FORMATS") requestType = "Format"

  const message = `${companyName} has requested ${requestType} for ${formattedDetails}.`

  const newNotification = {
    category: "Document Request",
    title: "New Document Request",
    message,
    gifUrl: "/Images/New notification.gif",
    timestamp: getIndianTimestamp(),
    seen: false,
  }

  try {
    await set(newNotifRef, newNotification)
    console.log("Notification sent successfully")
  } catch (error) {
    console.error("Failed to send notification:", error)
  }
}

// Save request in document_requests node
const saveDocumentRequest = async (effectiveUserId, category, rawDetails, companyName) => {
  const formattedDetails = formatDetails(category.toLowerCase(), rawDetails)
  
  const requestRef = dbRef(db, `document_requests/${effectiveUserId}/${category.toLowerCase()}`)
  const newRequestRef = push(requestRef)
   let requestType = "Document"
  if (category === "MTC") requestType = "MTC"
  else if (category === "STATEMENTS") requestType = "Statement"
  else if (category === "INVOICES") requestType = "Invoice"
  else if (category === "FORMATS") requestType = "Format"

  const message = `${companyName} has requested ${requestType} for ${formattedDetails}.`

  const requestData = {
    details: formattedDetails,
    message,
    companyName,
    status: "pending",
    timestamp: getIndianTimestamp(),
  }

  try {
    await set(newRequestRef, requestData)
    return true
  } catch (error) {
    console.error("Failed to save request:", error)
    return false
  }
}

const Downloads = ({ category = "mtc", onClose, forcedUserId, companyName = "User" }) => {
  const { userId: hookUserId, loading: idLoading } = useGetCurrentUserId()
  const effectiveUserId = forcedUserId || hookUserId

  const [showInfoModal, setShowInfoModal] = useState(false)
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [pendingRequests, setPendingRequests] = useState([])

  // Form states
  const [selectedWeeks, setSelectedWeeks] = useState([])
  const [dateRange, setDateRange] = useState({ from: "", to: "" })
  const [selectedFormat, setSelectedFormat] = useState("")
  const [requestSent, setRequestSent] = useState(false)
  // New states for Invoices
  const [invoiceOption, setInvoiceOption] = useState("current") // "current", "previous", "custom"
  const [selectedMonthLabel, setSelectedMonthLabel] = useState("")

  useEffect(() => {
    if (category === "invoices") {
      if (invoiceOption === "current") {
        const range = getCurrentMonthRange()
        setSelectedMonthLabel(range.displayLabel)
        setDateRange({ from: range.from, to: range.to })
      } else if (invoiceOption === "previous") {
        const range = getPreviousMonthRange()
        setSelectedMonthLabel(range.displayLabel)
        setDateRange({ from: range.from, to: range.to })
      } else {
        setSelectedMonthLabel("")
        setDateRange({ from: "", to: "" })
      }
    }
  }, [invoiceOption, category])

  // Fetch files from Storage
  useEffect(() => {
    const fetchFiles = async () => {
      if (!effectiveUserId && category !== "formats") return
      if (idLoading) return

      setLoading(true)
      setError("")
      setFiles([])

      try {
        let storagePath = ""
        if (category === "mtc") storagePath = `mtc/${effectiveUserId}`
        else if (category === "statements") storagePath = `user-statements/${effectiveUserId}`
        else if (category === "invoices") storagePath = `invoices/${effectiveUserId}`
        else if (category === "formats") storagePath = `formats/${effectiveUserId}`

        const listRef = storageRef(storage, storagePath)
        const listResult = await listAll(listRef)

        const filePromises = listResult.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef)
          return { name: itemRef.name, url }
        })

        const fileList = await Promise.all(filePromises)
        setFiles(fileList.sort((a, b) => a.name.localeCompare(b.name)))
      } catch (err) {
        console.error("Storage error:", err)
        setError("No files found or access denied.")
      } finally {
        setLoading(false)
      }
    }

    fetchFiles()
  }, [effectiveUserId, category, idLoading])

  // Listen to pending requests
  useEffect(() => {
    if (!effectiveUserId) return

    const requestsRef = dbRef(db, `document_requests/${effectiveUserId}/${category.toLowerCase()}`)
    
    const unsubscribe = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const requestsArray = Object.entries(data).map(([id, req]) => ({ id, ...req }))
        setPendingRequests(requestsArray.filter(req => req.status === "pending"))
      } else {
        setPendingRequests([])
      }
    })

    return () => unsubscribe()
  }, [effectiveUserId, category])

  const handleRequest = async () => {
    let rawDetails = ""

    if (category === "mtc") {
      if (selectedWeeks.length === 0) return alert("Please select at least one week")
      const sortedWeeks = [...selectedWeeks].sort((a, b) => a - b)
      rawDetails = sortedWeeks.join(",")
    } 
    else if (category === "statements") {
      const { from, to } = dateRange
      if (!from || !to) return alert("Please select both From and To dates")
      rawDetails = `${formatDateForDisplay(from)} to ${formatDateForDisplay(to)}`
    } 
    else if (category === "invoices") {
      if (invoiceOption === "custom") {
        const { from, to } = dateRange
        if (!from || !to) return alert("Please select both From and To dates")
        if (new Date(from) > new Date(to)) return alert("From date cannot be later than To date")
        rawDetails = `${formatDateForDisplay(from)} to ${formatDateForDisplay(to)}`
      } else {
        rawDetails = selectedMonthLabel   // "April 2026" or "March 2026"
      }
    } 
    else if (category === "formats") {
      if (!selectedFormat) return alert("Please select a document format")
      rawDetails = selectedFormat
    } 
    else return

    const saved = await saveDocumentRequest(effectiveUserId, category, rawDetails, companyName)

    if (saved) {
      await sendRequestNotification(effectiveUserId, companyName, category.toUpperCase(), rawDetails)
      setRequestSent(true)

      // Reset
      if (category === "mtc") setSelectedWeeks([])
      else if (category === "formats") setSelectedFormat("")
      else if (category === "invoices") {
        setInvoiceOption("current")
      } else {
        setDateRange({ from: "", to: "" })
      }

      setTimeout(() => setRequestSent(false), 5000)
    }
  }

  const getTitle = () => {
    switch (category) {
      case "mtc": return "MTC Files"
      case "statements": return "Statements"
      case "invoices": return "Invoices"
      case "formats": return "Document Formats"
      default: return "Downloads"
    }
  }

  const handleDownload = (url, name) => {
    const link = document.createElement("a")
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getInfoContent = () => {
    switch (category) {
      case "mtc":
        return (
          <>
            <h3>How to get your MTC</h3>
            <ul>
              <li>Select the required weeks (1–52) using the checkboxes above</li>
              <li>You can select single or multiple weeks as needed</li>
              <li>Click <strong>Request</strong></li>
              <li>You will receive notification when MTC is uploaded</li>
              <li>Once uploaded, files appear here automatically</li>
              <li>You can then <strong>View</strong> or <strong>Download</strong> them</li>
            </ul>
          </>
        );

      case "statements":
      case "invoices":
        return (
          <>
            <h3>How to get your {category === "statements" ? "Statements" : "Invoices"}</h3>
            <ul>
              <li>Use the calendar fields above to select From and To dates</li>
              <li>Click <strong>Request</strong></li>
              <li>You will receive notification when documents are uploaded</li>
              <li>Files will appear here automatically once ready</li>
            </ul>
          </>
        );

      case "formats":
        return (
          <>
            <h3>Document Formats</h3>
            <ul>
              <li>PO - Purchase Order format on Letter Head</li>
              <li>TDS/TCS below 10 cr</li>
              <li>TDS/TCS above 10 cr</li>
              <li>Select the required format and click <strong>Request</strong></li>
              <li>You will be notified once the document is ready</li>
            </ul>
          </>
        );

      default:
        return <p>No information available.</p>
    }
  }

  return (
    <>
      <div className="file-popup-overlay" onClick={onClose} />

      <div className="dfile-popup">
        <div className="file-popup-header">
          <div className="header-center">
            <h2>{getTitle()}</h2>
            <button className="D-info" onClick={() => setShowInfoModal(true)} title="How it works">
              <MdInfo />
            </button>
          </div>
          <button className="dclose-btn" onClick={onClose}>×</button>
        </div>

        <div className="file-popup-content">
          {/* Request Form */}
          {(category === "mtc" || category === "statements" || category === "invoices" || category === "formats") && (
            <div className="request-section">
              {category === "mtc" ? (
                <>
                  <label className="input-label">Select Weeks (1-52)</label>
                  <p className="helper-text">You can select single or multiple weeks</p>
                  <div className="weeks-grid">
                    {Array.from({ length: 52 }, (_, i) => {
                      const weekNum = i + 1
                      return (
                        <div key={weekNum} className="week-option">
                          <input
                            type="checkbox"
                            id={`week-${weekNum}`}
                            checked={selectedWeeks.includes(weekNum)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedWeeks(prev => [...prev, weekNum])
                              } else {
                                setSelectedWeeks(prev => prev.filter(w => w !== weekNum))
                              }
                            }}
                          />
                          <label htmlFor={`week-${weekNum}`} className="week-label">Week {weekNum}</label>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : category === "formats" ? (
                <>
                  <label className="input-label">Select Document Format</label>
                  <select 
                    className="request-input format-select"
                    value={selectedFormat}
                    onChange={(e) => setSelectedFormat(e.target.value)}
                  >
                    <option value="">-- Select Format --</option>
                    <option value="PO">PO (Purchase Order)</option>
                    <option value="TDS/TCS below 10 cr">TDS/TCS below 10 cr</option>
                    <option value="TDS/TCS above 10 cr">TDS/TCS above 10 cr</option>
                  </select>
                </>
              ) : category === "invoices" ? (
                <>
                  <label className="input-label">Select Invoice Period</label>
                  
                  <div className="invoice-options">
                    <button 
                      className={`option-btn ${invoiceOption === "current" ? "active" : ""}`}
                      onClick={() => setInvoiceOption("current")}
                    >
                      Current Month
                    </button>
                    <button 
                      className={`option-btn ${invoiceOption === "previous" ? "active" : ""}`}
                      onClick={() => setInvoiceOption("previous")}
                    >
                      Previous Month
                    </button>
                    <button 
                      className={`option-btn ${invoiceOption === "custom" ? "active" : ""}`}
                      onClick={() => setInvoiceOption("custom")}
                    >
                      Custom Range
                    </button>
                  </div>

                  {/* Show Selected Month Name */}
                  {(invoiceOption === "current" || invoiceOption === "previous") && selectedMonthLabel && (
                    <p className="selected-month">
                      📅 Selected: <strong>{selectedMonthLabel}</strong>
                    </p>
                  )}

                  {/* Custom Date Range - only show when custom is selected */}
                  {invoiceOption === "custom" && (
                    <div className="date-range-inputs">
                      <div className="date-input-group">
                        <label className="date-sub-label">From</label>
                        <input
                          type="date"
                          value={dateRange.from}
                          onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                          className="request-input date-picker"
                        />
                      </div>
                      <div className="date-input-group">
                        <label className="date-sub-label">To</label>
                        <input
                          type="date"
                          value={dateRange.to}
                          onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                          className="request-input date-picker"
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label className="input-label">Select Date Range</label>
                  <div className="date-range-inputs">
                    <div className="date-input-group">
                      <label className="date-sub-label">From</label>
                      <input
                        type="date"
                        value={dateRange.from}
                        onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                        className="request-input date-picker"
                      />
                    </div>
                    <div className="date-input-group">
                      <label className="date-sub-label">To</label>
                      <input
                        type="date"
                        value={dateRange.to}
                        onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                        className="request-input date-picker"
                      />
                    </div>
                  </div>
                </>
              )}
              

              <button className="dbtn-request" onClick={handleRequest}>
                Request
              </button>

              {requestSent && (
                <p className="success-msg">✓ Request sent successfully! You will be notified when the document is ready.</p>
              )}
            </div>
          )}

          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <div className="pending-requests">
              <h4><FaClock /> Waiting for Documents</h4>
              <div className="pending-list">
                {pendingRequests.map((req, idx) => (
                  <div key={idx} className="pending-item">
                    <span>{req.details}</span>
                   <span>{" "}Requested:</span> <strong>{req.timestamp}</strong>
                  </div>
                ))}
              </div>
              <p className="pending-note">
                These documents are being processed. They will appear below once uploaded.
              </p>
            </div>
          )}

          {/* Documents List */}
         <div className="documents-info-banner">
            <div className="info-icons">
              <MdInfo />
            </div>
            <p>
              {
                 `All available ${getTitle().toLowerCase()} are displayed below. You can also request new documents using the above request section.`}
            </p>
          </div>

          {loading ? (
            <p className="status-text">Loading files...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : files.length === 0 && pendingRequests.length === 0 ? (
            <p className="status-text">
              No {getTitle().toLowerCase()} available yet. Use the request section above.
            </p>
          ) : (
           <div className="file-list">
              {files.map((file, idx) => (
                <div key={idx} className="file-row">
                  <div className="ds-file-info">
                    <div className="ds-file-icon">
                      <FaFileAlt />
                    </div>
                    <div className="file-name" title={file.name}>
                      {file.name}
                    </div>
                  </div>

                  <div className="file-actions">
                    <button
                      className="dw-dbtn-view"
                      onClick={() => window.open(file.url, "_blank")}
                      title="View"
                    >
                      <IoEyeSharp />
                    </button>
                    <button
                      className="dw-dbtn-down"
                      onClick={() => handleDownload(file.url, file.name)}
                      title="Download"
                    >
                      <FaDownload />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="file-popup-footer">
          <button className="btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <>
          <div className="info-modal-overlay" onClick={() => setShowInfoModal(false)} />
          <div className="info-modal">
            <div className="info-modal-header">
              <h3>Information</h3>
              <button className="info-close-btn" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            <div className="info-modal-body">
              {getInfoContent()}
            </div>
            <div className="info-modal-footer">
              <button className="btn-outline" onClick={() => setShowInfoModal(false)}>
                Got it
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default Downloads